<template>
  <section class="home home--connect" id="HeaderWhite">
    <div class="container">
      <div class="row">
        <div class="col-xl-8 col-lg-12 align-self-center">
          <div class="home__title" ref="titleElement">
            <HelpersSpotCircle />
            <div class="home__text">{{ translate("ready.to") }}</div>
            <div class="home__highlight">{{ translate("connect") }}</div>
          </div>
        </div>
        <div class="col-xl-4 col-lg-12">
          <a href="/en/contact-us">
            <div class="home__circle" ref="circleElement">
              <div class="home__wrapper">
                <div class="home__slide" ref="textContainer">
                  <div class="home__list">{{ translate("success") }}</div>
                  <div class="home__list">{{ translate("idea") }}</div>
                  <div class="home__list">{{ translate("customer") }}</div>
                  <div class="home__list">{{ translate("business") }}</div>
                  <div class="home__list">{{ translate("sales") }}</div>
                </div>
              </div>
              <div class="home__link">
                {{ translate("let.start.the.project") }}<i class="icon-arrow-button"></i>
              </div>
            </div>
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { useTranslationStore } from "@/stores/translations";

import { ref, onMounted, onBeforeUnmount } from "vue";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// ดึง store สำหรับการแปล
const translationStore = useTranslationStore();

// ดึงข้อมูลการแปลเมื่อ component ถูก mount
const fetchTranslations = async () => {
  await translationStore.fetchTranslations();
};

// สร้างฟังก์ชันสำหรับการแปล
const translate = translationStore.translate;

gsap.registerPlugin(ScrollTrigger);

const textContainer = ref(null);
const titleElement = ref(null);
const circleElement = ref(null);
const homeConnectElement = ref(null);
let currentY = 0;
let intervalId = null;

const scrollText = () => {
  const containerHeight = textContainer.value.parentNode.offsetHeight;
  const textHeight = textContainer.value.scrollHeight;
  const lineHeight = textContainer.value.firstChild.offsetHeight;

  currentY -= lineHeight;

  if (Math.abs(currentY) + containerHeight >= textHeight) {
    currentY = 0;
  }

  textContainer.value.style.transform = `translateY(${currentY}px)`;
};

const initialiseGSAP = () => {
  ScrollTrigger.create({
    trigger: ".home--connect",
    start: "top 50%",
    onEnter: () => {
      gsap.fromTo(
        titleElement.value,
        { opacity: 0, y: 100, x: 100 },
        { opacity: 1, y: 0, x: 0, duration: 0.6, ease: "power1.inOut" }
      );
      gsap.fromTo(
        circleElement.value,
        { opacity: 0, y: 100, x: -100 },
        { opacity: 1, y: 0, x: 0, duration: 0.6, ease: "power1.inOut" }
      );
    },
    onLeaveBack: () => {
      gsap.set(titleElement.value, { opacity: 0, y: 100, x: 100 });
      gsap.set(circleElement.value, { opacity: 0, y: 100, x: -100 });
    },
    markers: false,
  });
};

const initialiseServiceGSAP = () => {
  ScrollTrigger.create({
    trigger: ".home--service",
    start: "top top",
    onEnter: () => {
      initialiseGSAP();
    },
    markers: false,
  });
};

onMounted(() => {
  intervalId = setInterval(scrollText, 1000);
  initialiseServiceGSAP();
});

onBeforeUnmount(() => {
  clearInterval(intervalId);
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/app";

@each $lang in $languages {
  html[lang="#{$lang}"] {
    .home {
      &.home--connect {
        .home__title {
          @include font-size("display-3", $lang);
          @include font-weight("bold", $lang);

          @include media-breakpoint-down(lg) {
            @include font-size("h1", $lang);
          }
          @include media-breakpoint-down(sm) {
            @include font-size("h3", $lang);
          }
        }

        .home__highlight {
          @include font-family("highlight", $lang);
        }

        .home__list {
          @include font-size("h4", $lang);
          @include font-weight("regular", $lang);
        }

        .home__link {
          @include font-size("body", $lang);
          @include font-weight("medium", $lang);
        }
      }
    }
  }
}

.home {
  &.home--connect {
    position: relative;
    z-index: 1;
    background-color: $black;
    color: $white;
    padding: 8rem 0;
    overflow: hidden;

    @include media-breakpoint-down(lg) {
      padding: 4rem 0;
    }

    .home__title {
      opacity: 0;
      transform: translate(100px, 100px);
      transition: opacity 0.6s, transform 0.6s;

      @include media-breakpoint-down(lg) {
        text-align: center;
        margin-bottom: 3rem;
      }
    }

    .home__circle {
      width: 320px;
      height: 320px;
      border-radius: 320px;
      border: 1px solid $white;
      color: $white;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      @include transition(0.3s);

      opacity: 0;
      transform: translate(-100px, 100px);
      transition: opacity 0.6s, transform 0.6s;

      @include hover {
        background-color: $yellow-ci;
        color: $black;
        transform: translate(-7px, -4px);
        border: 0;
      }

      @include media-breakpoint-down(lg) {
        margin-left: auto;
        margin-right: auto;
      }

      @include media-breakpoint-down(sm) {
        width: 280px;
        height: 280px;
      }
    }

    .home__highlight {
      font-style: italic;
      padding-left: 5rem;

      @include media-breakpoint-down(lg) {
        padding-left: 0;
      }
    }

    .home__wrapper {
      width: 100%;
      height: 80px; /* ความสูงของ 1 บรรทัด */
      overflow: hidden;
      position: relative;
      text-align: center;
    }
    .home__slide {
      position: absolute;
      top: 0;
      width: 100%;
      transition: transform 0.5s ease-in-out; /* สมูทขึ้น */
    }

    .home__list {
      padding: 0.5rem 0;
    }

    .home__link {
      i {
        font-size: px-to-rem(14);
        margin-left: 0.25rem;
      }
    }
  }
}
</style>
